import React, { useEffect, useRef, useState } from "react";

import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { UI_SIZE } from "../../enums";
import { trackEvent } from "../../helpers/analyticsHelpers";
import { getRecommendationUrl } from "../../helpers/areenaLinks";
import { usePrevious } from "../../helpers/commonUtils";
import {
  fetchRecommendations,
  Recommendation
} from "../../service/areena-recommendations";
import { usePlayerStore } from "../../store/usePlayerStore";
import { PlayerUpdateEvent } from "../../types/common";
import { EndRecommendations } from "../recommendations/EndRecommendations";
import { PauseRecommendations } from "../recommendations/PauseRecommendations";
import {
  CommonCardProps,
  getUiSize,
  SetPlayingRecommendation,
  UiSize
} from "../recommendations/recommendationTypes";

const recommendationsShownEvent = (
  recommendations: Recommendation[],
  uiSize: UiSize
) => {
  const recommendedIds = recommendations.map(r => r.id).join(",");
  trackEvent("player.recommendations.shown", {
    yle_ids: recommendedIds,
    ...(uiSize !== UiSize.Small && {
      main: recommendations[0].id
    })
  });
};

export default function Recommendations({
  disabled,
  hasNextEpisode,
  hideRecommendationsOverlay,
  newPlayerUi = false,
  showRecommendationsOverlay,
  uiRef,
  update
}: {
  disabled: boolean;
  hasNextEpisode: boolean;
  hideRecommendationsOverlay?: () => void;
  newPlayerUi?: boolean;
  showRecommendationsOverlay?: () => void;
  uiRef?: HTMLDivElement | null;
  update: PlayerUpdateEvent;
}) {
  const { subscribe, useStoreState } = usePlayerStore();
  const durationInSeconds = useStoreState.durationInSeconds();
  const isEnded = useStoreState.isEnded();
  const isFullscreen = useStoreState.isFullscreen();
  const isPaused = useStoreState.isPaused();
  const isSeeking = useStoreState.isSeeking();
  const uiSize = useStoreState.uiSize();
  const programId = useStoreState.programId();
  const uiSizeInPx = useStoreState.uiSizeInPx();

  const previousIsFullScreen = usePrevious(isFullscreen);
  const previousIsSeeking = usePrevious(isSeeking);

  const { language } = useEmbedParametersContext();

  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [recommendationsFetched, setRecommendationsFetched] =
    useState<boolean>(false);
  const [showEndRecommendations, setShowEndRecommendations] =
    useState<boolean>(false);
  const [showPauseRecommendations, setShowPauseRecommendations] =
    useState<boolean>(false);
  const [shouldFetchRecommendations, setShouldFetchRecommendations] =
    useState<boolean>(false);
  const [blockPauseRecommendations, setBlockPauseRecommendations] =
    useState<boolean>(false);
  const [recommendationsShown, setRecommendationsShown] =
    useState<boolean>(false);

  const playingRecommendationIndexRef = useRef<undefined | number>(undefined);
  const programIdRef = useRef(programId);
  const languageRef = useRef(language);

  useEffect(() => {
    programIdRef.current = programId;
  }, [programId]);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    if (isEnded) {
      setShowPauseRecommendations(false);
      if (!hasNextEpisode) {
        setShowEndRecommendations(true);
      }
    }
  }, [isEnded, hasNextEpisode]);

  useEffect(() => {
    if (
      uiSize === UI_SIZE.LARGE &&
      isPaused &&
      !isEnded &&
      !blockPauseRecommendations
    ) {
      setShouldFetchRecommendations(true);
      setShowPauseRecommendations(true);
    }
  }, [uiSize, isPaused, blockPauseRecommendations, isEnded]);

  useEffect(() => {
    if (!isPaused) {
      uiRef?.focus();
      setShowEndRecommendations(false);
      setShowPauseRecommendations(false);
    }
  }, [isPaused, uiRef]);

  useEffect(() => {
    if (uiSize !== UI_SIZE.LARGE) {
      setShowPauseRecommendations(false);
      setShowEndRecommendations(false);
    }
  }, [uiSize]);

  useEffect(() => {
    if (hasNextEpisode || !durationInSeconds || shouldFetchRecommendations) {
      return;
    }
    const unsubscribe = subscribe(
      state => state.currentTime,
      currentTime => {
        if (durationInSeconds - currentTime < 5) {
          setShouldFetchRecommendations(true);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [
    durationInSeconds,
    hasNextEpisode,
    shouldFetchRecommendations,
    subscribe
  ]);

  const setPlayingRecommendation: SetPlayingRecommendation = index =>
    (playingRecommendationIndexRef.current = index);

  useEffect(() => {
    if (
      programIdRef.current &&
      !isFullscreen &&
      previousIsFullScreen &&
      playingRecommendationIndexRef.current !== undefined
    ) {
      window.location.assign(
        getRecommendationUrl({
          autoplay: true,
          index: playingRecommendationIndexRef.current,
          language: languageRef.current,
          programId: programIdRef.current
        })
      );
    }
  }, [isFullscreen, previousIsFullScreen]);

  useEffect(() => {
    if (!isSeeking && previousIsSeeking) {
      setShowEndRecommendations(false);
    }
  }, [isSeeking, previousIsSeeking]);

  useEffect(() => {
    if (showEndRecommendations || showPauseRecommendations) {
      showRecommendationsOverlay?.();
    } else {
      hideRecommendationsOverlay?.();
    }
  }, [
    hideRecommendationsOverlay,
    showEndRecommendations,
    showPauseRecommendations,
    showRecommendationsOverlay
  ]);

  const previousProgramId = useRef(programId);

  useEffect(() => {
    if (previousProgramId.current !== programId) {
      setRecommendationsFetched(false);
    }
  }, [programId]);

  useEffect(() => {
    if (programId && shouldFetchRecommendations && !recommendationsFetched) {
      setRecommendationsFetched(true);
      fetchRecommendations(programId, language).then(response => {
        setRecommendations(response);
      });
    }
  }, [language, programId, recommendationsFetched, shouldFetchRecommendations]);

  useEffect(() => {
    if (
      recommendationsFetched &&
      !recommendationsShown &&
      recommendations.length > 0 &&
      (showPauseRecommendations || showEndRecommendations)
    ) {
      setRecommendationsShown(true);
      recommendationsShownEvent(recommendations, getUiSize(uiSizeInPx));
    }
  }, [
    recommendationsFetched,
    recommendationsShown,
    recommendations,
    uiSizeInPx,
    showPauseRecommendations,
    showEndRecommendations
  ]);

  const onClose = () => {
    setShowPauseRecommendations(false);
    setBlockPauseRecommendations(true);
  };

  const commonCardProps: CommonCardProps = {
    language,
    newPlayerUi,
    setPlayingRecommendation,
    uiSize: getUiSize(uiSizeInPx),
    update
  };

  return (
    <>
      <EndRecommendations
        recommendations={recommendations}
        commonCardProps={commonCardProps}
        showRecommendations={!!recommendations.length && showEndRecommendations}
      />
      <PauseRecommendations
        commonCardProps={commonCardProps}
        disabled={disabled}
        onClose={onClose}
        recommendations={recommendations}
        showRecommendations={
          !!recommendations.length && showPauseRecommendations
        }
      />
    </>
  );
}
